/**
 * ChallengeParticipantSwitcher styles.
 * Row: label left, horizontal chips (mobile) or dropdown (desktop/web) right.
 */

import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

const CHIP_AVATAR_SIZE = 24;

/** Min width (px) to show horizontal chips; below this use dropdown. */
export const PARTICIPANT_SWITCHER_DROPDOWN_MIN_WIDTH = 420;

/** Breakpoint below which dropdown uses compact (smaller) sizing. */
export const PARTICIPANT_SWITCHER_COMPACT_MAX_WIDTH = 380;

export function getChallengeParticipantSwitcherStyles(theme: Theme, screenWidth?: number) {
  const compact = screenWidth != null && screenWidth < PARTICIPANT_SWITCHER_COMPACT_MAX_WIDTH;
  const triggerPaddingV = compact ? 8 : 14;
  const triggerPaddingH = compact ? 10 : 14;
  const triggerMinHeight = compact ? 40 : 48;
  const triggerFontSize = compact ? 13 : 14;
  const listMaxHeight = compact ? 180 : 220;
  const itemPaddingV = compact ? 10 : 12;
  const itemPaddingH = compact ? 10 : 12;
  const itemFontSize = compact ? 13 : 14;
  const wrapMinWidth = compact ? 130 : 160;
  const wrapMaxWidth = compact ? 200 : 240;

  return StyleSheet.create({
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
      paddingHorizontal: 0,
    },
    label: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray[700],
      marginRight: 8,
    },
    scrollView: {
      flexGrow: 0,
    },
    chipsContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 6,
      flex: 1,
      justifyContent: 'flex-end',
    },
    chip: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 4,
      paddingHorizontal: 8,
      borderRadius: 20,
      borderWidth: 1.5,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
    },
    chipSelected: {
      borderColor: theme.primary,
      backgroundColor: theme.primary + '15',
    },
    chipContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    chipImage: {
      width: CHIP_AVATAR_SIZE,
      height: CHIP_AVATAR_SIZE,
      borderRadius: CHIP_AVATAR_SIZE / 2,
    },
    chipLabel: {
      fontSize: 12,
      fontWeight: '500',
      color: theme.gray[800],
      maxWidth: 72,
    },
    chipLabelSelected: {
      color: theme.primary,
      fontWeight: '600',
    },
    dropdownWrap: {
      alignSelf: 'flex-end',
      minWidth: wrapMinWidth,
      maxWidth: wrapMaxWidth,
    },
    dropdownWrapOpen: {
      zIndex: 99999,
      elevation: 24,
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: triggerPaddingV,
      paddingHorizontal: triggerPaddingH,
      minHeight: triggerMinHeight,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      gap: 8,
    },
    dropdownTriggerOpen: {
      borderColor: theme.primary,
      backgroundColor: theme.primary + '08',
    },
    dropdownTriggerText: {
      fontSize: triggerFontSize,
      fontWeight: '600',
      color: theme.gray[800],
      flex: 1,
    },
    dropdownTriggerPlaceholder: {
      color: theme.gray[500],
      fontWeight: '500',
    },
    dropdownChevron: {
      fontSize: 12,
      color: theme.gray[500],
    },
    modalBackdrop: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    dropdownListModal: {
      position: 'absolute',
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      maxHeight: listMaxHeight,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.12,
      shadowRadius: 8,
      elevation: 24,
    },
    dropdownList: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      marginTop: 4,
      borderRadius: 10,
      borderWidth: 1.5,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      zIndex: 99999,
      maxHeight: listMaxHeight,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.12,
      shadowRadius: 8,
      elevation: 24,
    },
    dropdownItem: {
      paddingVertical: itemPaddingV,
      paddingHorizontal: itemPaddingH,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[200],
    },
    dropdownItemLast: {
      borderBottomWidth: 0,
    },
    dropdownItemText: {
      fontSize: itemFontSize,
      fontWeight: '500',
      color: theme.gray[800],
    },
    dropdownItemTextSelected: {
      fontWeight: '700',
      color: theme.primary,
    },
  });
}

export const CHALLENGE_PARTICIPANT_SWITCHER_AVATAR_SIZE = CHIP_AVATAR_SIZE;
